"use client";

import { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

export type LightboxImage = {
  src: string;
  alt?: string;
};

export function ImageLightbox({
  open,
  images,
  index,
  onOpenChange,
  onIndexChange,
}: {
  open: boolean;
  images: LightboxImage[];
  index: number;
  onOpenChange: (open: boolean) => void;
  onIndexChange: (index: number) => void;
}) {
  const current = images[index];
  const hasMany = images.length > 1;

  useEffect(() => {
    if (!open || !hasMany) {
      return;
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === "ArrowLeft") {
        onIndexChange((index - 1 + images.length) % images.length);
      }
      if (event.key === "ArrowRight") {
        onIndexChange((index + 1) % images.length);
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasMany, images.length, index, onIndexChange, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-0 left-0 flex h-[100dvh] max-h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 items-center justify-center rounded-none border-0 bg-black/90 p-4 text-white shadow-none sm:max-w-none">
        <DialogTitle className="sr-only">Kép megtekintése</DialogTitle>
        <DialogDescription className="sr-only">
          {current?.alt ?? "Csatolt kép nagyobb nézetben."}
        </DialogDescription>
        {current ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={current.src}
            alt={current.alt ?? "Csatolt kép"}
            className="max-h-[calc(100dvh-2rem)] max-w-full object-contain"
          />
        ) : null}
        {hasMany ? (
          <>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute top-1/2 left-3 size-11 -translate-y-1/2 rounded-full bg-white/90 text-slate-900"
              aria-label="Előző kép"
              onClick={() =>
                onIndexChange((index - 1 + images.length) % images.length)
              }
            >
              <ChevronLeft className="size-5" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute top-1/2 right-12 size-11 -translate-y-1/2 rounded-full bg-white/90 text-slate-900 md:right-16"
              aria-label="Következő kép"
              onClick={() => onIndexChange((index + 1) % images.length)}
            >
              <ChevronRight className="size-5" />
            </Button>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
